import { useState } from 'react'

import {
  CreateExtraHourRangeDocument,
  CreateExtraHourRangeMutationVariables,
  ExtraHoursRangeFragmentDoc,
  FetchExtraHoursRangesDocument,
  UpdateExtraHourRangeDocument,
  UpdateExtraHourRangeMutationVariables,
} from 'generated/resources/graphql'

import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { convertHoursToBackendMinutes } from 'modules/attendance/components/SettingItemPage/TimeTracking/WorkTimeCategories/Content/utils/extraHoursUtils'
import { timeRangeCategoryTypeEnum } from 'modules/attendance/lib/enums'
import { ExtraHoursRulesData } from 'modules/attendance/lib/forms/utils/ExtraHourRulesWizardData'
import useHandleReumbursableMappings from 'modules/attendance/lib/forms/utils/useHandleReimbursableMappings'
import { minutesToCents } from 'modules/attendance/lib/timeConverter'
import { EMPLOYEE_TYPE } from 'modules/attendance/lib/types'
import type AttendanceSetting from 'modules/attendance/store/models/AttendanceSetting'
import useDeleteReimbursablePayrollConcept from 'modules/compensations/hooks/useDeleteReimbursablePayrollConcept'
import useUpsertReimbursablePayrollConcept from 'modules/compensations/hooks/useUpsertReimbursablePayrollConcept'

export const useExtraHourRules = ({
  settingId,
  attendanceSetting: _attendanceSetting,
  hasContractHoursBaseline,
  companyId,
}: {
  settingId: string
  attendanceSetting: AttendanceSetting
  hasContractHoursBaseline: boolean
  companyId: string
}) => {
  const [create] = useMutation(CreateExtraHourRangeDocument, {
    extractorFn: (data) => data.attendanceMutations.createAttendanceExtraHoursRange,
    update: (cache, { data }) => {
      const newExtraHoursRangeRule =
        data?.attendanceMutations.createAttendanceExtraHoursRange?.extraHoursRange

      if (!newExtraHoursRangeRule) return

      cache.modify({
        id: `AttendanceEmployeesSetting:${settingId}`,
        fields: {
          extrahoursrangeConnection(existingData, { readField }) {
            const existingIds = new Set<string>(
              existingData.nodes.map((item: { _ref: string }) => readField('id', item))
            )

            const existsExtraHourRule = existingIds.has(newExtraHoursRangeRule.id)

            if (existsExtraHourRule) return existingData

            const addedExtraHourRule = cache.writeFragment({
              data: newExtraHoursRangeRule,
              fragment: ExtraHoursRangeFragmentDoc,
              fragmentName: 'ExtraHoursRange',
            })

            return {
              ...existingData,
              nodes: [...existingData.nodes, addedExtraHourRule],
              totalCount: existingData.totalCount + 1,
            }
          },
        },
      })
    },
  })

  const [updateExtraHourRange] = useMutation(UpdateExtraHourRangeDocument, {
    extractorFn: (data) => data.attendanceMutations.updateAttendanceExtraHoursRange,
  })

  const update = (variables: UpdateExtraHourRangeMutationVariables) => {
    return updateExtraHourRange({
      variables,
      refetchQueries: [FetchExtraHoursRangesDocument],
      awaitRefetchQueries: true,
    })
  }

  const [errors, setErrors] = useState({})
  const { manageMappings } = useHandleReumbursableMappings()
  const { upsert: upsertCompensationMapping } = useUpsertReimbursablePayrollConcept()
  const { deleteReimbursablePayrollConcept } = useDeleteReimbursablePayrollConcept({
    companyId,
    reimbursableTypes: ['TimeSettings::CustomTimeRangeCategory'],
  })

  const handleUpsertCompensationMapping = async (data: ExtraHoursRulesData) => {
    if (!data.payrollConceptId) return

    const reimbursableId =
      data.timeSettingsCustomTimeRangeCategoryId != null
        ? String(data.timeSettingsCustomTimeRangeCategoryId)
        : undefined

    if (!reimbursableId) return

    const existingCompensationMapping = data.payrollMappingsCompensations?.find(
      (mapping) => mapping.reimbursableId === reimbursableId
    )

    if (data.poolType === 'virtual_payroll') {
      const result = await upsertCompensationMapping({
        id: existingCompensationMapping?.id ? String(existingCompensationMapping.id) : undefined,
        payrollConceptId: data.payrollConceptId,
        reimbursableId,
        reimbursableType: timeRangeCategoryTypeEnum.TimeSettingsCustomTimeRangeCategory,
      })

      return handleResult(result, setErrors)
    } else {
      if (existingCompensationMapping?.id) {
        const result = await deleteReimbursablePayrollConcept({
          id: String(existingCompensationMapping.id),
        })
        return handleResult(result, setErrors)
      }
      return { success: true }
    }
  }

  const updateOnSubmit = async (data: UpdateExtraHourRangeMutationVariables) => {
    const result = await update(data)
    const handled = handleResult(result, (updateErrors) => {
      setErrors(updateErrors)
    })

    return handled.success
  }

  const createOnSubmit = async (data: CreateExtraHourRangeMutationVariables) => {
    const result = await create({ variables: data })
    const handled = handleResult(result, (createErrors) => {
      setErrors(createErrors)
    })

    return handled.success
  }

  const onSubmit = async (data: ExtraHoursRulesData) => {
    const { fromMinute, toMinute } = convertHoursToBackendMinutes(data.fromHour, data.toHour)

    // Determine baseline based on user selection if available;
    // fallback to previous behavior (feature-flag driven) for minimal change
    const baseline =
      data.baseline ??
      (!data.isDefault && hasContractHoursBaseline ? 'contract_hours' : 'effective_hours')

    // Use employeeType from data (set explicitly when creating, or from existing rule when editing)
    const employeeType = data.employeeType || EMPLOYEE_TYPE.FULL_TIME

    const variables = {
      timeSettingsCustomTimeRangeCategoryId: data.timeSettingsCustomTimeRangeCategoryId ?? '',
      poolType: data.poolType,
      attendanceEmployeesSettingId: data.attendanceEmployeesSettingId,
      equivalentMinutesInCents: minutesToCents(data.equivalentMinutes),
      rawMinutesInCents: minutesToCents(data.rawMinutes),
      fromMinute: fromMinute,
      toMinute: toMinute,
      unlimited: data.isUnlimited(),
      baseline: baseline,
      employeeType: employeeType,
    }

    const success = data.id
      ? await updateOnSubmit({
          ...variables,
          id: data.id,
        })
      : await createOnSubmit(variables)

    if (success) {
      //TODO: Remove this once the new compensation module is fully released
      manageMappings(setErrors, data.formMappingsTaxonomies ?? [])

      const compensationMappingResult = await handleUpsertCompensationMapping(data)

      const ok = compensationMappingResult?.success ?? true
      if (ok) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
        })
        return true
      } else {
        return false
      }
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('errors.generic_error'),
      })
      return false
    }
  }

  return { onSubmit, errors }
}
